import { CloseOutlined, FilterOutlined } from '@ant-design/icons';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    TenantToolbarFilterBtn,
    TenantToolbarFilterBtnOuter,
    TenantToolbarFilterClearBadge,
} from '@/pages/inside/tenant/style';

/**
 * Toolbar filter control: opens a modal for editing filter draft; Apply commits.
 * When `hasActiveFilters`, a small clear control appears on the top-right of the trigger.
 *
 * @param {string} title - Modal title
 * @param {boolean} open
 * @param {(open: boolean) => void} onOpenChange
 * @param {boolean} hasActiveFilters - Applied filters are active (shows clear badge)
 * @param {() => void} onClearApplied - Clears committed filters (badge); should close modal if open
 * @param {() => void} onApply - Copies draft → applied and closes modal (parent owns draft state)
 * @param {() => void} onClearDraft - Clears in-modal draft only (e.g. Clear all)
 * @param {React.ReactNode} children - Modal body (forms, fields bound to draft in parent/hook)
 * @param {'sm'|'md'|'lg'} [size='md']
 * @param {string} [filterLabel='Filters']
 */
export function ToolbarFilterButton({
    title,
    open,
    onOpenChange,
    hasActiveFilters,
    onClearApplied,
    onApply,
    onClearDraft,
    children,
    size = 'md',
    filterLabel = 'Filters',
    bodyStyle,
}) {
    return (
        <>
            <TenantToolbarFilterBtnOuter>
                <TenantToolbarFilterBtn
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-label={filterLabel}
                    onClick={() => onOpenChange(true)}
                >
                    <FilterOutlined aria-hidden />
                    {filterLabel}
                </TenantToolbarFilterBtn>
                {hasActiveFilters ? (
                    <TenantToolbarFilterClearBadge
                        type="button"
                        aria-label="Clear filters"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onClearApplied?.();
                        }}
                    >
                        <CloseOutlined aria-hidden />
                    </TenantToolbarFilterClearBadge>
                ) : null}
            </TenantToolbarFilterBtnOuter>

            <AppModal
                size={size}
                title={title}
                open={open}
                onCancel={() => onOpenChange(false)}
                footer={null}
                styles={{ body: { paddingTop: 8, ...(bodyStyle && typeof bodyStyle === 'object' ? bodyStyle : {}) } }}
            >
                <CreateRoleModalBody>{children}</CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} type="button" onClick={onClearDraft} $padding="10px 16px" $size="14px">
                        Clear all
                    </Button>
                    <Button $primary $blackOnLight type="button" onClick={onApply} $padding="10px 16px" $size="14px">
                        Apply
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </>
    );
}
